import { doc, writeBatch } from 'firebase/firestore';
import { db } from '../../firebase/firebase';
import { AlbumManager } from '../AlbumManager';
import { PinManager } from '../PinManager';
import { ProjectManager } from '../ProjectManager';
import { getRecordingsByProjectKey } from '../recording/queries';
import { generateAlbumKey } from '../../utilities/albumKeyGenerator';
import { DemoDataManager } from '../../services/demoDataManager';
import { isDemoModeWithTutorial } from '../base/demoModeUtils';
import type { Album } from '../../types';
import type { IProject } from '../../types/Project';
import {
  buildEpisodePinMigrationUpdate,
  buildEpisodeRecordingMigrationUpdates,
  pinNeedsEpisodeMigration,
  resolveEpisodeAlbumKeyForMigration,
  type EpisodeMigrationResult,
} from '../../utils/episodeProjectMigration';

const BATCH_SIZE = 400;

async function ensureEpisodeAlbumExists(
  episodeAlbumKey: string,
  project: IProject
): Promise<void> {
  const existing = await AlbumManager.getAlbum(episodeAlbumKey);
  if (existing) return;

  const album: Album = {
    albumKey: episodeAlbumKey,
    name: `${project.projectName ?? 'Project'} — Episode Album`,
    projectKey: project.project_id,
    userKey: null,
  };
  await AlbumManager.createAlbum(album);
}

async function commitBatches<T>(
  items: T[],
  applyItem: (batch: ReturnType<typeof writeBatch>, item: T) => void
): Promise<void> {
  for (let i = 0; i < items.length; i += BATCH_SIZE) {
    const chunk = items.slice(i, i + BATCH_SIZE);
    const batch = writeBatch(db);
    chunk.forEach((item) => applyItem(batch, item));
    await batch.commit();
  }
}

async function migrateFirestoreProjectToEpisode(project_id: string): Promise<EpisodeMigrationResult> {
  const project = await ProjectManager.getProject(project_id);
  if (!project) {
    throw new Error(`Project ${project_id} not found`);
  }

  const pins = await ProjectManager.getProjectPins(project_id);
  let episodeAlbumKey = resolveEpisodeAlbumKeyForMigration(project.episodeAlbumKey, pins);
  if (!episodeAlbumKey) {
    episodeAlbumKey = await generateAlbumKey();
  }

  await ensureEpisodeAlbumExists(episodeAlbumKey, project);

  const isQREnabled = project.isQREnabled === true;
  const pinsToUpdate = pins.filter((pin) => pinNeedsEpisodeMigration(pin, episodeAlbumKey!));

  await commitBatches(pinsToUpdate, (batch, pin) => {
    const pinRef = doc(db, 'Pins', pin.pinKey);
    batch.update(pinRef, buildEpisodePinMigrationUpdate(pin, episodeAlbumKey!, isQREnabled));
  });

  const introRecordingKey = project.settings?.introRecordingKey;
  const recordings = await getRecordingsByProjectKey(project_id);
  const recordingUpdates = buildEpisodeRecordingMigrationUpdates(
    recordings,
    episodeAlbumKey,
    introRecordingKey
  ).filter(({ key, updates }) => {
    const recording = recordings.find((r) => r.key === key);
    if (!recording) return false;
    return recording.albumKey !== updates.albumKey || recording.sequenceNumber !== updates.sequenceNumber;
  });

  await commitBatches(recordingUpdates, (batch, { key, updates }) => {
    const recordingRef = doc(db, 'Recordings', key);
    batch.update(recordingRef, updates);
  });

  if (project.episodeAlbumKey !== episodeAlbumKey) {
    await ProjectManager.setEpisodeAlbumKey(project_id, episodeAlbumKey);
  }

  return {
    episodeAlbumKey,
    pinsUpdated: pinsToUpdate.length,
    recordingsUpdated: recordingUpdates.length,
  };
}

function migrateDemoTutorialProjectToEpisode(project_id: string): EpisodeMigrationResult {
  const demoProjects = DemoDataManager.getDemoOrganisationProjects();
  const project = demoProjects.find((p) => p.project_id === project_id);
  if (!project) {
    throw new Error(`Demo project ${project_id} not found`);
  }

  const pins = DemoDataManager.getPins(project_id);
  let episodeAlbumKey = resolveEpisodeAlbumKeyForMigration(project.episodeAlbumKey, pins);
  if (!episodeAlbumKey) {
    throw new Error('Cannot migrate demo project without pins or an episode album key');
  }

  const isQREnabled = project.isQREnabled === true;
  let pinsUpdated = 0;

  for (const pin of pins) {
    if (!pinNeedsEpisodeMigration(pin, episodeAlbumKey)) continue;
    DemoDataManager.updatePin({
      ...pin,
      ...buildEpisodePinMigrationUpdate(pin, episodeAlbumKey, isQREnabled),
    });
    pinsUpdated += 1;
  }

  const recordings = DemoDataManager.getRecordings(project_id);
  const recordingUpdates = buildEpisodeRecordingMigrationUpdates(
    recordings,
    episodeAlbumKey,
    project.settings?.introRecordingKey
  );
  for (const { key, updates } of recordingUpdates) {
    DemoDataManager.updateRecording(key, updates);
  }

  DemoDataManager.updateDemoOrganisationProject(project_id, {
    episodeAlbumKey,
    isEpisodeProjectEnabled: true,
  });

  return {
    episodeAlbumKey,
    pinsUpdated,
    recordingsUpdated: recordingUpdates.length,
  };
}

export async function runEpisodeProjectMigration(
  project_id: string
): Promise<EpisodeMigrationResult> {
  if (isDemoModeWithTutorial('organisational-projects-tutorial')) {
    return migrateDemoTutorialProjectToEpisode(project_id);
  }
  return migrateFirestoreProjectToEpisode(project_id);
}

export async function applyEpisodeModeTransitionIfNeeded(
  project_id: string,
  previousEnabled: boolean | undefined,
  nextEnabled: boolean | undefined
): Promise<EpisodeMigrationResult | null> {
  if (nextEnabled === true && previousEnabled !== true) {
    return runEpisodeProjectMigration(project_id);
  }
  return null;
}
